import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Search, Loader2, ShieldCheck, AlertCircle, Check, KeyRound, UserCheck, LogIn } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminLegacyActivatePage() {
  const navigate = useNavigate();
  // 대시보드/다른 관리자 페이지와 동일하게 AuthContext 기준으로 권한 확인 (불일치 제거)
  const { user, token, loading: authLoading } = useAuth();
  const isAdmin = !!user && (user.role === "admin" || user.is_admin || (user.email || "").toLowerCase() === "admin@newcoupling.com");

  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [activating, setActivating] = useState(false);

  const search = async () => {
    if (query.trim().length < 2) {
      toast.error("최소 2자 이상 입력해주세요.");
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/legacy/lookup?q=${encodeURIComponent(query.trim())}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "검색 실패");
      setResults(data.results || []);
      if ((data.results || []).length === 0) {
        toast.message("일치하는 legacy 회원이 없습니다.");
      }
    } catch (e) {
      toast.error(e.message || "검색 중 오류");
    } finally {
      setBusy(false);
    }
  };

  const activate = async () => {
    if (!selected) return;
    if (newPassword.length < 6) {
      toast.error("비밀번호는 6자 이상이어야 합니다.");
      return;
    }
    setActivating(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/legacy/force-activate`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ email: selected.email, new_password: newPassword }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "활성화 실패");
      toast.success(`✅ ${selected.username || selected.email} 활성화 완료`);
      // refresh list
      await search();
      setSelected(null);
      setNewPassword("");
    } catch (e) {
      toast.error(e.message || "활성화 실패");
    } finally {
      setActivating(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 pt-24 pb-16 px-4">
      <div className="max-w-3xl mx-auto">
        <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-burgundy-600 mb-4">
          <ArrowLeft className="w-4 h-4" /> 어드민 대시보드
        </Link>

        {/* === Auth gate (대시보드와 동일 기준) === */}
        {authLoading ? (
          <div className="bg-white rounded-3xl shadow-sm border border-stone-100 p-10 text-center">
            <Loader2 className="w-7 h-7 mx-auto text-stone-400 animate-spin mb-3" />
            <p className="text-sm text-stone-500">권한 확인 중…</p>
          </div>
        ) : !isAdmin ? (
          <div className="bg-white rounded-3xl shadow-sm border border-stone-100 p-8 sm:p-10 text-center">
            <ShieldCheck className="w-10 h-10 mx-auto text-burgundy-300 mb-3" />
            <h2 className="text-lg font-bold text-stone-900 mb-1">관리자 권한이 필요합니다</h2>
            <p className="text-sm text-stone-500 mb-6">
              {token
                ? "현재 계정은 관리자가 아니거나, 세션이 만료되었습니다."
                : "이 페이지는 관리자만 사용할 수 있습니다. 먼저 관리자 계정으로 로그인해주세요."}
            </p>
            <button
              onClick={() => navigate("/?login=1")}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-burgundy-600 hover:bg-burgundy-700 text-white font-semibold transition-colors"
              data-testid="admin-legacy-login-btn"
            >
              <LogIn className="w-4 h-4" />
              관리자 로그인하러 가기
            </button>
            <p className="text-[11px] text-stone-400 mt-5">
              관리자 이메일: <code className="bg-stone-100 px-1.5 py-0.5 rounded">admin@newcoupling.com</code>
            </p>
          </div>
        ) : (
        <div className="bg-white rounded-3xl shadow-sm border border-stone-100 p-6 sm:p-8">
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck className="w-6 h-6 text-burgundy-600" />
            <h1 className="text-xl font-bold text-stone-900">회원 비밀번호 즉시 재설정 (운영자 도구)</h1>
          </div>
          <p className="text-sm text-stone-500 mb-1">이메일 발송이 불가한 상황에서 회원의 비밀번호를 직접 설정해 즉시 활성화합니다.</p>
          <p className="text-xs text-stone-400 mb-6 flex items-start gap-1.5">
            <AlertCircle className="w-3.5 h-3.5 mt-0.5" />
            legacy(기존 LizTV)·신규 회원 모두 지원. 설정한 임시 비밀번호를 회원에게 안전하게 전달해주세요 (전화/카톡 등).
          </p>

          {/* 검색 */}
          <div className="flex gap-2 mb-4">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && search()}
              placeholder="이메일 또는 닉네임 (최소 2자)"
              className="flex-1 px-4 py-3 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-burgundy-400"
              data-testid="admin-legacy-search-input"
            />
            <button
              onClick={search}
              disabled={busy}
              className="px-5 py-3 rounded-xl bg-burgundy-600 hover:bg-burgundy-700 text-white font-semibold flex items-center gap-2 disabled:opacity-50"
              data-testid="admin-legacy-search-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              검색
            </button>
          </div>

          {/* 결과 리스트 */}
          {results.length > 0 && (
            <div className="space-y-2 mb-6">
              {results.map((u) => {
                const isActive = u.legacy_account_activated || u.active;
                const isSelected = selected?.email === u.email;
                return (
                  <button
                    key={u.email}
                    onClick={() => setSelected(u)}
                    className={`w-full text-left px-4 py-3 rounded-xl border transition-colors ${
                      isSelected
                        ? "border-burgundy-500 bg-burgundy-50"
                        : "border-stone-200 hover:border-burgundy-300 hover:bg-stone-50"
                    }`}
                    data-testid={`admin-legacy-result-${u.email}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-semibold text-stone-800 truncate">{u.username || u.name || "(이름 없음)"}</div>
                        <div className="text-sm text-stone-500 truncate">{u.email}</div>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        {u.is_legacy && (
                          <span className="inline-flex items-center text-[10px] px-2 py-0.5 bg-amber-50 text-amber-700 rounded-full">
                            LizTV
                          </span>
                        )}
                        {isActive ? (
                          <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 bg-emerald-50 text-emerald-700 rounded-full">
                            <UserCheck className="w-3 h-3" /> 활성
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 bg-stone-50 text-stone-600 rounded-full">
                            미활성
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* 선택된 회원에 대한 활성화 폼 */}
          {selected && (
            <div className="bg-burgundy-50/50 border border-burgundy-200 rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-3">
                <KeyRound className="w-5 h-5 text-burgundy-600" />
                <h3 className="font-bold text-stone-900">
                  {selected.username || selected.email} 의 임시 비밀번호 설정
                </h3>
              </div>
              <input
                type="text"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="임시 비밀번호 (6자 이상)"
                className="w-full px-4 py-3 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-burgundy-400 mb-3"
                data-testid="admin-legacy-tmp-password"
              />
              <div className="flex gap-2">
                <button
                  onClick={() => { setSelected(null); setNewPassword(""); }}
                  className="px-4 py-2.5 rounded-xl border border-stone-200 text-stone-600 hover:bg-stone-50"
                  data-testid="admin-legacy-cancel"
                >
                  취소
                </button>
                <button
                  onClick={activate}
                  disabled={activating || newPassword.length < 6}
                  className="flex-1 px-4 py-2.5 rounded-xl bg-burgundy-600 hover:bg-burgundy-700 text-white font-semibold flex items-center justify-center gap-2 disabled:bg-stone-300 disabled:cursor-not-allowed"
                  data-testid="admin-legacy-activate-btn"
                >
                  {activating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  즉시 활성화
                </button>
              </div>
              <p className="text-[11px] text-stone-500 mt-3 leading-relaxed">
                활성화 후 위 임시 비밀번호를 회원에게 안전하게 전달하세요. 회원은 로그인 후 마이페이지에서 비밀번호를 변경할 수 있습니다.
              </p>
            </div>
          )}
        </div>
        )}
      </div>
    </div>
  );
}
